import React, { useEffect, useMemo, useState } from "react";
import { api, unwrap, errMsg } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { LockKeyhole, Save, ScrollText } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { useI18n } from "@/i18n";

export default function TeacherMarks() {
  const { t } = useI18n();
  const { data: exams } = useQuery({ queryKey: ["exams"], queryFn: async () => unwrap(await api.get("/exams")) });
  const [examId, setExamId] = useState("");
  const [scheduleId, setScheduleId] = useState("");

  useEffect(() => { if (exams?.length) setExamId((id) => id || exams[0].id); }, [exams]);

  const { data: exam } = useQuery({
    queryKey: ["exam", examId],
    queryFn: async () => unwrap(await api.get(`/exams/${examId}`)),
    enabled: !!examId,
  });

  const schedules = useMemo(() => exam?.schedules || [], [exam]);
  useEffect(() => {
    setScheduleId((id) => {
      if (schedules[0] && !id) return schedules[0].id;
      if (id && !schedules.find((s) => s.id === id)) return schedules[0]?.id || "";
      return id;
    });
  }, [schedules]);

  const { data: marksData, refetch, isLoading } = useQuery({
    queryKey: ["marks", scheduleId],
    queryFn: async () => unwrap(await api.get(`/exam-schedules/${scheduleId}/marks`)),
    enabled: !!scheduleId,
  });

  const [values, setValues] = useState({});
  useEffect(() => {
    if (marksData) {
      const map = {};
      (marksData.marks || []).forEach((m, i) => {
        const stu = marksData.students[i];
        if (m) map[stu.id] = { marks_obtained: m.marks_obtained ?? "", is_absent: m.is_absent || false, remark: m.remark || "" };
      });
      setValues(map);
    }
  }, [marksData]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const entries = (marksData.students || []).map((s) => {
        const v = values[s.id] || {};
        return { student_id: s.id, marks_obtained: v.is_absent ? null : (v.marks_obtained === "" ? null : Number(v.marks_obtained)), is_absent: !!v.is_absent, remark: v.remark || null };
      });
      return unwrap(await api.post(`/exam-schedules/${scheduleId}/marks`, { schedule_id: scheduleId, entries }));
    },
    onSuccess: (d) => { toast.success(t("teacher.savedEntriesToast", { count: d.saved })); refetch(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const schedule = schedules.find((s) => s.id === scheduleId);
  const students = marksData?.students || [];

  return (
    <div className="space-y-5" data-testid="teacher-marks">
      <div>
        <h1 className="font-display text-2xl font-bold text-[var(--text-primary)]">{t("teacher.marksEntry")}</h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">{t("teacher.marksSubtitle")}</p>
      </div>

      <Card className="border-[var(--subtle)]">
        <CardContent className="p-4 space-y-3">
          <div>
            <Label className="text-[10px] uppercase font-semibold tracking-widest text-[var(--text-muted)]">{t("teacher.exam")}</Label>
            <Select value={examId} onValueChange={setExamId}>
              <SelectTrigger className="mt-1" data-testid="marks-exam"><SelectValue placeholder={t("teacher.chooseExam")} /></SelectTrigger>
              <SelectContent>{(exams || []).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase font-semibold tracking-widest text-[var(--text-muted)]">{t("teacher.subject")}</Label>
            <Select value={scheduleId} onValueChange={setScheduleId}>
              <SelectTrigger className="mt-1" data-testid="marks-schedule"><SelectValue placeholder={t("teacher.subject")} /></SelectTrigger>
              <SelectContent>
                {schedules.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.subject?.name} · {formatDate(s.exam_date)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {schedule?.locked && (
        <div className="flex items-center gap-2 text-[var(--warning)] bg-[var(--warning-bg)] px-4 py-3 rounded-md text-sm">
          <LockKeyhole className="h-4 w-4" /> {t("teacher.marksLockedWarning")}
        </div>
      )}

      {isLoading ? <Skeleton className="h-96" /> : students.length === 0 ? (
        <Card className="border-[var(--subtle)]"><CardContent className="py-10 text-center text-[var(--text-muted)]">
          <ScrollText className="h-8 w-8 mx-auto mb-2 opacity-40" /> {t("teacher.pickExamEmpty")}
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {students.map((s, i) => {
            const v = values[s.id] || {};
            return (
              <div key={s.id} className="flex items-center gap-2 rounded-md border border-[var(--subtle)] bg-white p-3">
                <div className="h-10 w-10 rounded-full bg-[var(--brand)] text-white flex items-center justify-center font-semibold text-sm flex-shrink-0">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{s.first_name} {s.last_name}</div>
                  <div className="text-[10px] text-[var(--text-muted)] font-mono-num">{s.admission_no}</div>
                </div>
                <label className="flex items-center gap-1 text-xs text-[var(--text-secondary)]">
                  <input type="checkbox" checked={!!v.is_absent} onChange={(e) => setValues({ ...values, [s.id]: { ...v, is_absent: e.target.checked } })} data-testid={`absent-${s.id}`} />
                  {t("teacher.absentCheckbox")}
                </label>
                <Input
                  type="number"
                  min="0"
                  max={schedule?.max_marks || 100}
                  placeholder={`/ ${schedule?.max_marks || 100}`}
                  value={v.is_absent ? "" : (v.marks_obtained ?? "")}
                  disabled={v.is_absent || schedule?.locked}
                  onChange={(e) => setValues({ ...values, [s.id]: { ...v, marks_obtained: e.target.value } })}
                  className="w-24 h-9 font-mono-num"
                  data-testid={`marks-${s.id}`}
                />
              </div>
            );
          })}
        </div>
      )}

      {students.length > 0 && (
        <div className="sticky bottom-20 md:bottom-4 z-20">
          <Button
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending || schedule?.locked}
            data-testid="save-marks"
            className="w-full h-14 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white font-semibold text-base shadow-lg"
          >
            <Save className="h-4 w-4 mr-2" /> {saveMut.isPending ? t("teacher.saving") : t("teacher.saveMarksFor", { count: students.length })}
          </Button>
        </div>
      )}
    </div>
  );
}
